import { CheckIcon } from '@heroicons/react/24/solid';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@superfunnel/ui-core/v2/popover';
import { Filter } from 'iconoir-react';
import React, { useCallback, useMemo } from 'react';
import { capitalize, classNames } from '../../utils';

interface Props {
  children:
    | React.ReactElement<FilterButtonOptionProps<any>>[]
    | React.ReactElement<FilterButtonOptionProps<any>>;
}

export const FilterButton = ({ children }: Props) => {
  React.Children.forEach(children, (child) => {
    if (
      child.type !== FilterButtonOption &&
      (child.type as any).name !== 'FilterButtonOption'
    ) {
      throw new Error('All children must be of type FilterButtonOption');
    }
  });

  const allFiltersCount = useMemo(() => {
    let count = 0;
    React.Children.forEach(children, (child) => {
      count += child.props.selected.length;
    });
    return count;
  }, [children]);

  const onResetButtonClick = useCallback(() => {
    React.Children.forEach(children, (child) => {
      child.props.onChange([]);
    });
  }, [children]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" color="white" className="h-full w-full">
          <Filter className="h-5 w-5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="end" className="w-auto p-3">
        <div className="relative flex gap-4">
          <Button
            color="primary"
            disabled={!allFiltersCount}
            className={classNames(
              allFiltersCount ? 'visible' : 'invisible',
              'absolute -right-1.5',
            )}
            onClick={onResetButtonClick}
          >
            Reset
          </Button>

          {children}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export interface FilterButtonOptionProps<T> {
  title: string;
  selected: string[];
  options: T[];
  getOptionKey: (option: T) => string;
  getOptionLabel: (option: T) => string;
  onChange: (selected: string[]) => void;
  noOptionsMessage?: React.ReactNode;
}

export function FilterButtonOption<T>({
  title,
  selected,
  options,
  getOptionKey,
  getOptionLabel,
  onChange,
  noOptionsMessage,
}: FilterButtonOptionProps<T>) {
  return (
    <div className="flex min-w-28 flex-col gap-2">
      <h6>{title}</h6>

      <div className="flex max-h-44 flex-col gap-1 overflow-scroll">
        {options.map((option) => {
          const optionKey = getOptionKey(option);
          const isSelected = selected.some((t) => t === optionKey);
          return (
            <Button
              key={optionKey}
              variant={'outline'}
              size="sm"
              onClick={() => {
                onChange(
                  isSelected
                    ? selected.filter((t) => t !== optionKey)
                    : [...(selected || []), optionKey],
                );
              }}
              className="relative justify-start ps-4 pe-9"
            >
              <CheckIcon
                className={classNames(
                  'absolute right-2 h-4 w-4',
                  isSelected ? 'visible' : 'invisible',
                )}
              />

              {capitalize(getOptionLabel(option))}
            </Button>
          );
        })}
        {options.length === 0 ? (
          <p className="text-sm text-gray-500">
            {noOptionsMessage ?? 'No Options'}
          </p>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
}
